import { Component } from "react";
import { DashboardRow, JobInputRow, JobPlatform, JobRow, JobStatus, PlatformRow, ReasonRow } from "namirasoft-job-arranger";
import { DateTimeFormatter, NSLine, NSLoading, NSRouterMakerResult, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { JobArrangerProps } from "../JobArrangerProps";
import { JobStatusObject } from "../JobStatusObject";
import Styles from "./JobStatusPanel.module.css";

export interface JobStatusPanelProps extends NSRouterMakerResult, JobArrangerProps
{
    job: JobRow;
    onChange?: (row: JobRow) => void;
}

export interface JobStatusPanelState
{
    job: JobRow;
    reasons: ReasonRow[] | null;
    dashboard: DashboardRow | null;
    platforms: PlatformRow[];
    loading: boolean;
}

export class JobStatusPanel extends Component<JobStatusPanelProps, JobStatusPanelState>
{
    private readonly dateFormatter = new DateTimeFormatter();

    constructor(props: JobStatusPanelProps)
    {
        super(props);
        this.state = { job: this.props.job, reasons: null, dashboard: null, platforms: [], loading: true };
    }

    override componentDidMount(): void
    {
        this.load();
    }

    override componentDidUpdate(prevProps: Readonly<JobStatusPanelProps>): void
    {
        if (this.props.job.id !== prevProps.job.id)
            this.setState({ job: this.props.job, loading: true }, () => this.load());
    }

    async load()
    {
        try
        {
            const reason = await this.props.servers.job_arranger.reason.List([], 1, 1000, [], null);
            const dashboard = await this.getDashboard(this.state.job.company);
            const platforms = await this.props.servers.job_arranger.platform.List().catch(() => [] as PlatformRow[]);
            this.setState(prev => ({ ...prev, reasons: reason.rows, dashboard, platforms, loading: false }));
        }
        catch (error)
        {
            this.setLoading(false);
        }
    }
    private getPlatformIcon(name: string): string | undefined
    {
        return this.state.platforms.find(p => p.name === name)?.icon;
    }

    // Refetch the job so the status_{name} dates reflect history edits made elsewhere.
    async reloadJob()
    {
        try
        {
            const job = await this.props.servers.job_arranger.job.Get(this.state.job.id);
            this.setState(prev => ({ ...prev, job }));
        }
        catch (error)
        {
            this.props.notifier.onError(error as Error);
        }
    }

    async getDashboard(company: string)
    {
        return await this.props.servers.job_arranger.company.GetDashboard(company);
    }

    async updateJob({ status, reason_id }: { status?: JobStatus, reason_id?: string })
    {
        if (
            (!status && !reason_id) ||
            (status === this.state.job.status) ||
            (reason_id === this.state.job.reason_id)
        )
            return;

        this.setLoading(true);

        let payload: JobInputRow = {
            workspace_id: this.state.job.workspace_id,
            sid: this.state.job.sid,
            name: this.state.job.name,
            status: status ?? this.state.job.status,
            reason_id: reason_id ?? this.state.job.reason_id,
            platform: this.state.job.platform,
            url: this.state.job.url,
            company: this.state.job.company,
            location: this.state.job.location,
            remote: this.state.job.remote,
            min: this.state.job.min,
            max: this.state.job.max,
            description: this.state.job.description,
            job_category: [],
            job_field: [],
            job_tag: [],
        }

        try
        {
            const job = await this.props.servers.job_arranger.job.Update(this.state.job.id, payload);
            const dashboard = await this.getDashboard(this.state.job.company);
            this.setState(
                prev => ({ ...prev, job, dashboard, loading: false }),
                () => this.props.onChange?.(job)
            );
        } catch (error)
        {
            this.setLoading(false);
        }
    }

    private setLoading(loading: boolean)
    {
        this.setState(prev => ({ ...prev, loading }));
    };

    private getStatusUpdatedAt(status: JobStatus): string | null
    {
        const key = `status_${status.toLowerCase()}` as keyof JobRow;
        const updated_at = this.state.job[key];

        if (!updated_at)
            return null;

        return this.dateFormatter.format(updated_at);
    }

    override render()
    {
        if (this.state.loading || !this.state.reasons || !this.state.dashboard)
            return <div className={Styles.ja_panel}><NSLoading /></div>;

        let filtered_reasons = this.state.reasons.filter(reason => (reason.status as string) === (this.state.job.status as string)) ?? [];
        let other_jobs = this.state.dashboard.jobs.filter(job => job.sid !== this.state.job.sid);

        return (
            <div className={Styles.ja_panel}>
                <h4 className={Styles.ja_panel_title}>Status:</h4>

                <NSRow style={{ justifyContent: "flex-start", gap: "0.75rem" }}>
                    {
                        JobStatusObject.getAll().map(status =>
                        {
                            const updated_at = this.getStatusUpdatedAt(status.name);
                            return (
                                <button
                                    key={status.name}
                                    className={Styles.ja_panel_button}
                                    style={{
                                        backgroundColor: this.state.job.status === status.name ? status.color : "none",
                                        color: this.state.job.status === status.name ? status.textColor : "none"
                                    }}
                                    onClick={() => this.updateJob({ status: status.name })}
                                    title={status.description}
                                >
                                    <span className={Styles.ja_panel_button_title}>{status.name}</span>
                                    {
                                        updated_at &&
                                        <span className={Styles.ja_panel_button_info}>{updated_at}</span>
                                    }
                                </button>
                            )
                        })
                    }
                </NSRow>

                {
                    filtered_reasons.length > 0 &&
                    <>
                        <NSSpace size={NSSpaceSizeType.MINI} />
                        <NSLine classList={[Styles.ja_panel_line]} />
                        <NSSpace size={NSSpaceSizeType.MINI} />

                        <h4 className={Styles.ja_panel_title}>Reason:</h4>

                        <NSRow style={{ justifyContent: "flex-start", gap: "0.75rem" }}>
                            {
                                filtered_reasons.map(reason => (
                                    <button
                                        key={reason.id}
                                        className={Styles.ja_panel_button}
                                        style={{
                                            backgroundColor: this.state.job.reason_id === reason.id ? JobStatusObject.get(reason.status)?.color : "",
                                            color: this.state.job.reason_id === reason.id ? JobStatusObject.get(reason.status)?.textColor : ""
                                        }}
                                        onClick={() => this.updateJob({ reason_id: reason.id })}
                                    >
                                        <span className={Styles.ja_panel_button_title}>{reason.name}</span>
                                    </button>
                                ))
                            }
                        </NSRow>
                    </>
                }

                <NSSpace size={NSSpaceSizeType.MINI} />
                <NSLine classList={[Styles.ja_panel_line]} />
                <NSSpace size={NSSpaceSizeType.MINI} />

                <h4 className={Styles.ja_panel_title}>Company: <strong>{this.state.job.company}</strong></h4>

                <NSRow style={{ justifyContent: "flex-start", gap: "0.75rem" }}>
                    {
                        this.state.dashboard?.byStatus.map(item => (
                            <div key={item.name} className={Styles.ja_panel_company_item}>
                                <div
                                    className={Styles.ja_panel_company_item_color}
                                    style={{ background: JobStatusObject.get(item.name)?.color }}
                                />
                                <span>{item.name}: {item.count}</span>
                            </div>
                        ))
                    }
                    {
                        this.state.dashboard?.byPlatform.map(item => (
                            item.name !== JobPlatform.None
                                ?
                                <div key={item.name} className={Styles.ja_panel_company_item}>
                                    <img
                                        src={this.getPlatformIcon(item.name)}
                                        alt="platform"
                                        width="24"
                                        height="24"
                                    />
                                    <span>{item.name}: {item.count}</span>
                                </div>
                                :
                                <></>
                        ))
                    }
                </NSRow>

                {
                    other_jobs.length > 0 &&
                    <>
                        <NSSpace size={NSSpaceSizeType.MINI} />
                        <NSLine classList={[Styles.ja_panel_line]} />
                        <NSSpace size={NSSpaceSizeType.MINI} />

                        <h4 className={Styles.ja_panel_title}>The same company jobs:</h4>

                        <div className={Styles.ja_panel_table_wrapper}>
                            <table className={Styles.ja_panel_table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Location</th>
                                        <th>Platform</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        other_jobs.map((job) => (
                                            <tr
                                                key={job.id}
                                                style={{ background: JobStatusObject.get(job.status)?.color }}
                                            >
                                                <td style={{ fontWeight: "500" }}>
                                                    <a href={job.url} target="_blank" rel="noreferrer">
                                                        {job.name}
                                                    </a>
                                                </td>
                                                <td>{job.location}</td>
                                                <td>
                                                    {job.platform !== JobPlatform.None &&
                                                        <img
                                                            src={this.getPlatformIcon(job.platform)}
                                                            alt="Platform"
                                                            width="24"
                                                            height="24"
                                                            style={{ marginRight: "4px", verticalAlign: "middle" }}
                                                        />
                                                    }
                                                    {job.platform}
                                                </td>
                                                <td>{job.status} ({this.dateFormatter.format(job.created_at)})</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </>
                }
            </div>
        );
    }
}